import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useField } from 'formik';
import { useMemo, useState } from 'react';

import { Button } from '../../components/button/Button';
import { Combobox } from '../../components/combobox/Combobox';
import { InputWrapper } from '../../components/InputWrapper';
import { Language, TrailerType } from 'src/types/enums';
import { getFullUrl } from '../../utils/url';
import { openTab } from '../../utils/windows';
import { IMinimalVatRate } from '../order/vatRate/VatRateComboboxField';
import { useCustomers } from './models';
import { CreateCustomerDialog } from './components/CreateCustomerDialog';

export type Customer = {
  id: number;
  name: string;
  deactivationReason?: string | null;
  hasActiveIncasso: boolean;
  defaultTrailerTypes: TrailerType[];
  defaultVatRate?: IMinimalVatRate | null | undefined;
  language: Language;
};

const displayCustomer = (customer: Customer) => {
  if (customer.deactivationReason) {
    return `${customer.name} (gedeactiveerd)`;
  } else if (customer.hasActiveIncasso) {
    return `${customer.name} (incasso)`;
  } else {
    return customer.name;
  }
};

export interface ICustomerComboboxFieldProps {
  name: string;
  labelText?: string;
  helperText?: string;
  isDisabled?: boolean;
  showDeactivated?: boolean;
}

export function CustomerComboboxField(props: ICustomerComboboxFieldProps) {
  const { name, labelText = 'Klant', helperText, isDisabled, showDeactivated } = props;
  const [searchValue, setSearchValue] = useState('');
  const [createState, setCreateState] = useState({ isOpen: false, name: '' });
  const { data } = useCustomers({ search: searchValue });

  const customers = useMemo(() => {
    return (data ?? []).filter((v) => showDeactivated || !v.deactivationReason);
  }, [data, showDeactivated]);

  const [field, meta, helpers] = useField({ name });
  const selectedItem = field.value ?? null;

  const openCustomer = (id: number) => openTab(getFullUrl(`/internal/customers/${id}/general`));
  return (
    <div className="flex gap-2">
      <InputWrapper labelText={labelText} invalidText={meta.touched ? meta.error : undefined} helperText={helperText}>
        <Combobox
          keyName="id"
          placeholder="Zoek een klant..."
          onQueryChange={setSearchValue}
          display={displayCustomer}
          items={customers}
          selectedItem={selectedItem}
          onSelect={(val) => {
            helpers.setValue(val);
          }}
          createLabel="Maak klant aan"
          onCreate={(query) => setCreateState({ isOpen: true, name: query })}
          onBlur={() => helpers.setTouched(true)}
          isDisabled={isDisabled}
          isInvalid={Boolean(meta.touched && meta.error)}
          isNullable={true}
        />
      </InputWrapper>
      <div className="flex items-center">
        <Button
          color="secondary"
          onTrigger={() => {
            if (selectedItem) {
              openCustomer(selectedItem.id);
            }
          }}
          isDisabled={!selectedItem}
        >
          <MagnifyingGlassIcon className="w-4 h-4" />
        </Button>
      </div>

      <CreateCustomerDialog
        isOpen={createState.isOpen}
        initialName={createState.name}
        onClose={() => setCreateState({ isOpen: false, name: '' })}
        onCreated={(created) => {
          helpers.setValue(created);
          setCreateState({ isOpen: false, name: '' });
        }}
      />
    </div>
  );
}
